// Mobile filter bottom sheet component
// CREATED: Provides a mobile-optimized filter interface using BottomSheet
// Features:
// - Swipe-to-dismiss gesture support
// - Section-based filter groups
// - Touch-target optimized (min 48px per item)
// - Real-time filter application with visual feedback
// - Active filter counter in header
// - Clear all functionality

import { BottomSheet } from './BottomSheet';
import { Filter, X } from 'lucide-react';

export interface FilterGroup {
  id: string;
  label: string;
  options: Array<{
    value: string;
    label: string;
    count?: number;
  }>;
  selectedValues: string[];
}

interface MobileFilterBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  filterGroups: FilterGroup[];
  onChange: (groupId: string, values: string[]) => void;
  onClearAll: () => void;
}

export function MobileFilterBottomSheet({
  isOpen,
  onClose,
  filterGroups,
  onChange,
  onClearAll,
}: MobileFilterBottomSheetProps) {
  const totalActiveFilters = filterGroups.reduce(
    (sum, group) => sum + group.selectedValues.length,
    0
  );

  const handleToggleOption = (groupId: string, value: string, currentValues: string[]) => {
    if (currentValues.includes(value)) {
      onChange(groupId, currentValues.filter((v) => v !== value));
    } else {
      onChange(groupId, [...currentValues, value]);
    }
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Filtres"
      maxHeight="80vh"
    >
      <div className="flex flex-col h-full">
        {/* Header with clear all */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-600" />
            <span className="text-sm font-medium text-gray-700">
              {totalActiveFilters > 0
                ? `${totalActiveFilters} filtre${totalActiveFilters > 1 ? 's' : ''} actif${totalActiveFilters > 1 ? 's' : ''}`
                : 'Aucun filtre actif'}
            </span>
          </div>
          {totalActiveFilters > 0 && (
            <button
              onClick={onClearAll}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
              style={{ minHeight: '36px' }}
            >
              <X size={16} />
              Tout effacer
            </button>
          )}
        </div>

        {/* Filter groups */}
        <div className="flex-1 overflow-y-auto">
          {filterGroups.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              Aucun filtre disponible
            </div>
          ) : (
            filterGroups.map((group) => (
              <div key={group.id} className="border-b border-gray-100 last:border-b-0">
                {/* Group header */}
                <div className="px-6 py-3 bg-gray-50">
                  <h4 className="text-sm font-semibold text-gray-700">{group.label}</h4>
                  {group.selectedValues.length > 0 && (
                    <span className="inline-flex items-center justify-center mt-1 px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                      {group.selectedValues.length} sélectionné{group.selectedValues.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {/* Group options */}
                <div className="py-2">
                  {group.options.length === 0 ? (
                    <div className="px-6 py-4 text-sm text-gray-500">
                      Aucune option disponible
                    </div>
                  ) : (
                    group.options.map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center gap-3 px-6 py-3 hover:bg-gray-50 active:bg-gray-100 cursor-pointer transition-colors touch-target-min"
                        style={{ minHeight: '48px' }}
                      >
                        <input
                          type="checkbox"
                          checked={group.selectedValues.includes(option.value)}
                          onChange={() =>
                            handleToggleOption(group.id, option.value, group.selectedValues)
                          }
                          className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500 flex-shrink-0"
                        />
                        <span className="flex-1 text-sm text-gray-700 font-medium">
                          {option.label}
                        </span>
                        {option.count !== undefined && (
                          <span className="text-xs text-gray-500 font-medium">
                            {option.count}
                          </span>
                        )}
                      </label>
                    ))
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Sticky footer with apply button */}
        <div className="border-t border-gray-200 px-6 py-4 bg-white">
          <button
            onClick={onClose}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors shadow-sm"
            style={{ minHeight: '48px' }}
          >
            Appliquer les filtres
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
